/* global dataLayer */
import React, { useEffect } from 'react';

import {
  useSoyuzExperiment,
  useSoyuzData,
  trans
} from '@spotahome/soyuz/client';
import {
  ToastContainer,
  ThemeProvider,
  CookiesProvider,
  CookiesConsumer
} from '@spotahome/ui-library';

import { useHistory } from 'react-router-dom';
import Metrics from '@spotahome/soyuz-tracking';

import { TenantTypeProvider } from '@spotahome/marketplace-common/src/context/TenantTypeContext';

import { configureMetricsActions } from '@spotahome/marketplace-common/src/graphql/mutations/tenantActions';
import { getNumberActiveBanners } from '@spotahome/marketplace-common/src/components/HomeCardBanner/config';

import { UserInfoProvider } from '@spotahome/marketplace-common/src/context/UserInfoContext';
import { CitiesProvider } from '@spotahome/marketplace-common/src/context/CitiesContext';

import AdUrlService from '@spotahome/marketplace-common/src/utils/ad-url-service';

import InformationToastWrapper from '@spotahome/marketplace-common/src/components/InformationToast/index';
import AppBanner from '@spotahome/marketplace-common/src/components/AppBanner';
import ListingUnavailableModal from '@spotahome/marketplace-common/src/components/ListingUnavailableModal';
import OptInModalWrapper from '@spotahome/marketplace-common/src/components/OptInModalWrapper';
import SahContainer from '@spotahome/marketplace-common/src/components/SahContainer';
import LoginModal from '@spotahome/marketplace-common/src/components/login/LoginModal';
import SpottyCat from '@spotahome/marketplace-common/src/components/SpottyCat';

import CurrencyFormatter from '@spotahome/marketplace-common/src/utils/CurrencyFormatter';
import JsonLdOrganization from '@spotahome/marketplace-common/src/LEGACY/javascripts/microdata/components/JsonLdOrganization';
import { isSeoMode } from '@spotahome/marketplace-common/src/utils/seo-mode';

import '@spotahome/marketplace-common/src/scss/init.scss';

import { HighlightMarkersProvider } from './context/HighlightMarkersContext';
import { HotspotsProvider } from './context/HotspotsContext';

import { LOAD_MAP_COOKIE, LOAD_MAP_COOKIE_VALUE } from './utils/static-map';

import Search from './components/Search';

const MARKERS_PER_PAGE = 60;
const SEARCH_ALGORITHM_EXPERIMENT = 'SearchAlgorithm_exploration';

const getSortingAlgorithm = experimentVariant =>
  experimentVariant === 'original'
    ? 'DataScience-model5'
    : 'DataScience-model5-exploration';

const SearchPage = () => {
  const { [SearchPage.NAME]: requestedData } = useSoyuzData();

  const SearchAlgorithmExperiment = useSoyuzExperiment(
    SEARCH_ALGORITHM_EXPERIMENT
  );

  const {
    city,
    poi,
    maxPriceInCity,
    initialFiltersFromQuery,
    initialMarkerList = [],
    initialHomecards = {},
    isUserLogged,
    numberOfMarkers,
    areas,
    metadata,
    showUnavailableModal,
    locationString,
    locationCoordinates,
    deviceType,
    initialCookie,
    preloadedHomecards,
    cityList,
    isSafari,
    internalLinking
  } = requestedData;

  const countryCode = city?.country?.countryIsoCode;
  const currency = new CurrencyFormatter(city.currencyIsoCode);
  const IS_SEO_MODE = isSeoMode(initialFiltersFromQuery);
  const history = useHistory();

  useEffect(() => {
    configureMetricsActions();

    Metrics.actions.sendActionEvent(Metrics.actions.VIEW_SEARCH, {
      cityId: city.id
    });
  }, [city.id]);

  useEffect(() => {
    const { userId } = requestedData;
    if (userId && typeof dataLayer !== 'undefined') {
      dataLayer.push({
        userId
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const map =
    typeof window !== 'undefined' &&
    AdUrlService.getQueryParamValue('map') === 'true';

  const mapCenter =
    typeof window !== 'undefined' &&
    AdUrlService.getQueryParamValue('mapCenter');

  return (
    <CookiesProvider initialCookie={initialCookie}>
      <ThemeProvider flags={{ newButtons: true }}>
        <SahContainer>
          <AppBanner source="search" deviceType={deviceType} />
          <ToastContainer />
          {showUnavailableModal && <ListingUnavailableModal />}
          <InformationToastWrapper city={city.id} />
          <UserInfoProvider value={{ isUserLogged }}>
            <CitiesProvider initialCityInfo={city}>
              <HighlightMarkersProvider>
                <HotspotsProvider>
                  <TenantTypeProvider>
                    <CookiesConsumer>
                      {({ getCookie }) => (
                        <Search
                          locationString={locationString}
                          locationCoordinates={locationCoordinates}
                          history={history}
                          discounts={city.discounts}
                          sessionPoi={poi}
                          city={city}
                          countryCode={countryCode}
                          currency={currency}
                          maxPriceInCity={maxPriceInCity}
                          initialFiltersFromQuery={initialFiltersFromQuery}
                          seoMode={IS_SEO_MODE}
                          seoContent={metadata.content}
                          initialMarkerList={initialMarkerList}
                          initialHomecards={initialHomecards}
                          numberOfMarkers={numberOfMarkers}
                          sortingAlgorithm={getSortingAlgorithm(
                            SearchAlgorithmExperiment.get('behaviour')
                          )}
                          itemsPerPage={
                            MARKERS_PER_PAGE - getNumberActiveBanners(city.id)
                          }
                          areas={areas}
                          isUserLogged={isUserLogged}
                          map={map}
                          mapCenter={mapCenter}
                          preloadedHomecards={preloadedHomecards}
                          cityList={cityList}
                          isSafari={isSafari}
                          loadMap={
                            getCookie(LOAD_MAP_COOKIE) === LOAD_MAP_COOKIE_VALUE
                          }
                          internalLinking={
                            internalLinking.searchInternalLinking
                          }
                        />
                      )}
                    </CookiesConsumer>
                  </TenantTypeProvider>
                  <SpottyCat deviceType={deviceType} />
                </HotspotsProvider>
              </HighlightMarkersProvider>
            </CitiesProvider>
            <LoginModal />
          </UserInfoProvider>
          <OptInModalWrapper />
          <JsonLdOrganization description={trans('meta.description')} />
        </SahContainer>
      </ThemeProvider>
    </CookiesProvider>
  );
};

SearchPage.NAME = 'SearchPage';

export default SearchPage;
